import { useUnit } from 'effector-react';
import { generateUuid } from '@lib/uuid';
import { createPortalNote } from '@nx-frontend/shared/api';
import { calcNewBlockIndex } from '@nx-frontend/shared/blocks/utlis/calc-new-block-index';
import { useBlockCreate } from '@nx-frontend/shared/hooks';
import { addNoteTemplateEvent } from '@nx-frontend/shared/store/note-template-store';
import { $orgId, $portalId, $workspaceId } from '@nx-frontend/shared/store/portal-app';
import { $blocksByPlace } from '@nx-frontend/shared/store/portal-blocks';
import { $page } from '@nx-frontend/shared/store/portal-page';
import { VirtualNodePayload } from '@nx-frontend/shared/store/virtual-portal-node';
import { NoteBlock, NoteCreateTileType, PortalBlockType, TileType } from '@nx-frontend/shared/types';
import { generateNoteGlobalId, getNoteTemplateIdFromTile, getWrapperProps } from '@nx-frontend/shared/utils';

interface NoteCreatePayload {
  type: NoteCreateTileType;
  title?: string;
}

export const usePortalNoteCreate = (): ((
  payload: NoteCreatePayload,
  pagePayload?: VirtualNodePayload
) => Promise<{ type: TileType }>) => {
  const [portalId, workspaceId, orgId, { page_body: blocks }, page, addNoteTemplate] = useUnit([
    $portalId,
    $workspaceId,
    $orgId,
    $blocksByPlace,
    $page,
    addNoteTemplateEvent,
  ]);

  const blockCreate = useBlockCreate();

  return async (payload: NoteCreatePayload, pagePayload) => {
    const noteId = generateNoteGlobalId();
    const templateKey = payload.type === TileType.BLANK_PAGE ? undefined : getNoteTemplateIdFromTile(payload.type);
    const contentBlock: NoteBlock = {
      id: generateUuid(),
      templateKey,
      type: PortalBlockType.NOTE,
      title: payload.title ?? getTileTitle(payload.type),
      orgId,
      workspaceId,
      noteId: noteId,
      index: calcNewBlockIndex({ blocks }),
      place: 'page_body',
      colspan: 6,
      rowspan: 6,
      actions: [],
      wrapper: getWrapperProps('blank'),
      mediaSizes: ['base', 'md', 'lg'],
      enabled: true,
      removable: true,
      pageId: page.id,
      data: [],
    };

    await createPortalNote({ workspaceId, portalId, globalId: noteId });

    if (templateKey != null) {
      const newNoteState = {
        globalId: noteId,
        template: templateKey,
        inited: false,
      };

      addNoteTemplate(newNoteState);
    }

    blockCreate({ block: contentBlock, openPageAfterCreate: true, scrollToBlock: true }, pagePayload);

    return {
      type: payload.type,
    };
  };
};

function getTileTitle(tileType: TileType): string {
  switch (tileType) {
    case TileType.TABLE:
      return 'Table';
    case TileType.ONBOARDING:
      return 'Onboarding Information';
    case TileType.WELCOME_PAGE:
      return 'Welcome to Our Service!';
    case TileType.FAQ:
      return 'Frequently Asked Questions';
    case TileType.FLOW_GUIDES:
      return 'Guidelines for Your Workflow';
    case TileType.KANBAN:
      return 'Kanban';
    case TileType.TASK_LIST:
      return 'Task list';
    case TileType.BLANK_PAGE:
      return 'New Page';
    default:
      throw new Error('Invalid TileType');
  }
}
